import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { InitialsAvatar } from "../components/InitialsAvatar";
import { ParticipantPhotoCard } from "../components/ParticipantPhotoCard";
import { ParticipantComplianceCard } from "../components/ParticipantComplianceCard";
import { StringListCard } from "../components/StringListCard";
import { ParticipantCareSummary } from "../components/ParticipantCareSummary";
import { api } from "../lib/api";
import { useResource, formatDate, formatTime, formatDateTime, titleCase, daysUntil } from "../lib/hooks";
import { uploadFile, fileViewUrl, fileDownloadUrl } from "../lib/files";
import { Modal } from "../components/Modal";
import { useAuth } from "../context/AuthContext";
import { FilterBar, matches } from "../components/FilterBar";
import { EntityForm, Field, Option } from "../components/EntityForm";
import { companyCreateFields, PARTICIPANT_DOC_CATEGORIES } from "../lib/formOptions";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { FormFillDialog } from "../components/FormFillDialog";
import type {
    Assignment, Employee, FormSubmission, FormTemplate, Handover, Medication,
    Participant, ServiceNote, Shift,
} from "../lib/types";

type Tab = "overview" | "workers" | "roster" | "medications" | "notes" | "handovers"
    | "documents" | "records" | "goals" | "burndown" | "risk" | "forms" | "funding"
    | "invoices" | "settings";

const DOC_KINDS = [
    { value: "care_plan", label: "Care plan" },
    { value: "consent", label: "Consent" },
    { value: "risk_assessment", label: "Risk assessment" },
    { value: "other", label: "Other" },
];

interface ParticipantDoc {
    id: string;
    kind: string;
    category: string | null;
    note: string | null;
    file_id: string;
    file_name: string;
    company_name: string | null;
    uploaded_by_name: string | null;
    created_at: string;
}

interface ShiftCarePlan {
    id: string;
    file_name: string;
    created_at: string;
    shift_starts_at: string;
}

interface ParticipantInvoice {
    id: string;
    invoice_number: string;
    status: string;
    period_start: string;
    period_end: string;
    total: string;
    company_name: string;
}

/**
 * Upload a document about a participant. Managers use this when the provider
 * has not sent one from their side — same table, same visibility: assigned
 * workers can read it the moment it saves.
 */
const DocumentDialog = ({ participant, defaultKind, onClose, onSaved }: {
    participant: Participant;
    defaultKind: string;
    onClose: () => void;
    onSaved: () => void;
}) => {
    const [file, setFile] = useState<File | null>(null);
    const [kind, setKind] = useState(defaultKind);
    const [category, setCategory] = useState("");
    const [note, setNote] = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const save = async () => {
        if (!file) return;
        setBusy(true);
        setError(null);
        try {
            const stored = await uploadFile(file, "general");
            await api.post("/partner-documents", {
                fileId: stored.id, kind, participantId: participant.id,
                category: category || undefined,
                note: note.trim() || undefined,
            });
            onSaved();
        } catch (err) {
            setError(err instanceof Error ? err.message : "Upload failed");
            setBusy(false);
        }
    };

    return (
        <Modal title={`Add document — ${participant.full_name}`}
            subtitle="Visible to managers and to the workers assigned to this participant."
            onClose={onClose}
            footer={<>
                <button className="btn" disabled={busy || !file} onClick={() => void save()}>
                    {busy ? "Saving…" : "Save document"}
                </button>
                <button className="btn ghost" onClick={onClose} disabled={busy}>Cancel</button>
            </>}>
            {error && <div className="error-banner">{error}</div>}
            <div className="form-grid">
                <div className="field half">
                    <label htmlFor="pdoc-file">Document (max 10 MB)</label>
                    <input id="pdoc-file" type="file" accept=".pdf,.doc,.docx,.txt,image/*"
                        onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
                </div>
                <div className="field half">
                    <label htmlFor="pdoc-kind">Kind</label>
                    <select id="pdoc-kind" value={kind} onChange={(e) => setKind(e.target.value)}>
                        {DOC_KINDS.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
                    </select>
                </div>
                <div className="field half">
                    <label htmlFor="pdoc-cat">Folder (provider filing structure)</label>
                    <select id="pdoc-cat" value={category} onChange={(e) => setCategory(e.target.value)}>
                        <option value="">—</option>
                        {PARTICIPANT_DOC_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                </div>
                <div className="field">
                    <label htmlFor="pdoc-note">Note (optional)</label>
                    <input id="pdoc-note" value={note} maxLength={500}
                        onChange={(e) => setNote(e.target.value)}
                        placeholder="e.g. Updated plan effective this month" />
                </div>
            </div>
        </Modal>
    );
};

const DocumentsTable = ({ docs }: { docs: ParticipantDoc[] }) => (
    <div className="table-wrap">
        <table>
            <thead><tr><th>Kind</th><th>File</th><th>Note</th><th>From</th><th>Added</th></tr></thead>
            <tbody>
                {docs.map((doc) => (
                    <tr key={doc.id}>
                        <td>
                            <span className="chip info">{titleCase(doc.kind)}</span>
                            {doc.category && (
                                <div style={{ fontSize: "0.7rem", color: "var(--muted)", marginTop: 2 }}>{doc.category}</div>
                            )}
                        </td>
                        <td>
                            <a href={fileViewUrl(doc.file_id)} target="_blank" rel="noreferrer" style={{ fontWeight: 600 }}>
                                {doc.file_name}
                            </a>
                            {" "}
                            <a className="icon-btn" href={fileDownloadUrl(doc.file_id)}>Download</a>
                        </td>
                        <td style={{ color: "var(--ink-soft)" }}>{doc.note ?? "—"}</td>
                        <td style={{ fontSize: "0.8rem" }}>
                            {doc.company_name ?? "—"}
                            {doc.uploaded_by_name && (
                                <div style={{ fontSize: "0.72rem", color: "var(--muted)" }}>{doc.uploaded_by_name}</div>
                            )}
                        </td>
                        <td className="mono">{formatDate(doc.created_at)}</td>
                    </tr>
                ))}
                {docs.length === 0 && <tr><td colSpan={5} className="empty">Nothing here yet.</td></tr>}
            </tbody>
        </table>
    </div>
);

interface FundingRow {
    id: string;
    source: string;
    managed_by: string | null;
    catalogue_version: string | null;
    total: string | null;
    start_date: string | null;
    end_date: string | null;
    review_date: string | null;
    notes: string | null;
    is_active: boolean;
}

/**
 * Funding v2 (062) with live utilisation: plan-period funding records with
 * Allocated (everything rostered inside the window at the applied rate),
 * Delivered (the subset already worked) and Unallocated (what remains of the
 * total) — all computed server-side each load, never stored. Managers
 * add/edit; supervisors read; a partner sees their own participant's
 * records read-only through RLS. Exported: the partner portal shows the
 * same card.
 */
interface RecordTypeRow { id: string; name: string; requires_expiry: boolean }
interface ParticipantRecordRow {
    id: string;
    type_name: string;
    file_id: string | null;
    file_name: string | null;
    issued_on: string;
    expires_on: string | null;
    notes: string | null;
    days_remaining: number | null;
    status: "none" | "current" | "expiring" | "expired";
    ack_count: number;
    acknowledged_by_me: boolean;
}

const RECORD_CHIP: Record<ParticipantRecordRow["status"], string> =
    { current: "pass", expiring: "warn", expired: "block", none: "mute" };

/**
 * Participant records (migration 092): plans, consents and agreements with
 * expiry chips and the append-only "I have read this" trail. One current
 * record per type - recording a new one supersedes the old.
 */
interface GoalRow {
    id: string; title: string; description: string | null; target_date: string | null;
    status: "active" | "achieved" | "on_hold" | "closed";
    progress_count: number; latest_rating: string | null; latest_progress_on: string | null;
}
interface ProgressRow {
    id: string; note: string; rating: string | null; progress_date: string; author_name: string | null;
}

const GOAL_CHIP: Record<GoalRow["status"], string> = {
    active: "info", achieved: "pass", on_hold: "warn", closed: "mute",
};
const RATING_CHIP: Record<string, string> = { on_track: "pass", at_risk: "warn", achieved: "pass" };

/** Structured goals (096): the goal list plus an append-only progress diary. */
const GoalsCard = ({ participantId, mayManage }: { participantId: string; mayManage: boolean }) => {
    const goals = useResource<GoalRow[]>(`/participant-goals?participantId=${participantId}`);
    const [openGoal, setOpenGoal] = useState<string | null>(null);
    const diary = useResource<ProgressRow[]>(openGoal ? `/participant-goals/${openGoal}/progress` : null);
    const [title, setTitle] = useState("");
    const [targetDate, setTargetDate] = useState("");
    const [note, setNote] = useState("");
    const [rating, setRating] = useState("on_track");

    const addGoal = async () => {
        if (!title.trim()) return;
        await api.post("/participant-goals", {
            participantId, title: title.trim(),
            ...(targetDate ? { targetDate } : {}),
        });
        setTitle(""); setTargetDate("");
        goals.reload();
    };
    const addProgress = async () => {
        if (!openGoal || !note.trim()) return;
        await api.post(`/participant-goals/${openGoal}/progress`, { note: note.trim(), rating });
        setNote("");
        diary.reload(); goals.reload();
    };
    const setStatus = async (goalId: string, status: string) => {
        await api.post(`/participant-goals/${goalId}/status`, { status });
        goals.reload();
    };

    return (
        <div className="card">
            <div className="card-head"><h2>Goals</h2></div>
            <div className="card-body">
                {mayManage && (
                    <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", alignItems: "end", marginBottom: "0.7rem" }}>
                        <label style={{ fontSize: "0.8rem", flex: "1 1 260px" }}>New goal<br />
                            <input style={{ width: "100%" }} value={title} placeholder="What is the participant working toward?"
                                onChange={(e) => setTitle(e.target.value)} /></label>
                        <label style={{ fontSize: "0.8rem" }}>Target date<br />
                            <input type="date" value={targetDate} onChange={(e) => setTargetDate(e.target.value)} /></label>
                        <button className="btn small" disabled={!title.trim()} onClick={() => void addGoal()}>Add goal</button>
                    </div>
                )}
                {(goals.data ?? []).length === 0
                    ? <div className="empty">No goals recorded yet.</div>
                    : (goals.data ?? []).map((g) => (
                        <div key={g.id} style={{ borderTop: "1px solid var(--line-soft)", padding: "0.55rem 0" }}>
                            <div style={{ display: "flex", gap: "0.5rem", alignItems: "center", flexWrap: "wrap" }}>
                                <button className="btn ghost small" onClick={() => setOpenGoal(openGoal === g.id ? null : g.id)}>
                                    {openGoal === g.id ? "▾" : "▸"}
                                </button>
                                <strong style={{ flex: 1 }}>{g.title}</strong>
                                {g.latest_rating && (
                                    <span className={`chip ${RATING_CHIP[g.latest_rating] ?? "info"}`}>
                                        {g.latest_rating.replace(/_/g, " ")}
                                    </span>
                                )}
                                <span className={`chip ${GOAL_CHIP[g.status]}`}>{g.status.replace(/_/g, " ")}</span>
                                <span style={{ fontSize: "0.78rem", color: "var(--muted)" }}>
                                    {g.progress_count} progress note{g.progress_count === 1 ? "" : "s"}
                                    {g.target_date ? ` · target ${g.target_date.slice(0, 10)}` : ""}
                                </span>
                                {mayManage && g.status === "active" && (
                                    <>
                                        <button className="icon-btn" onClick={() => void setStatus(g.id, "achieved")}>Achieved</button>
                                        <button className="icon-btn" onClick={() => void setStatus(g.id, "on_hold")}>On hold</button>
                                    </>
                                )}
                                {mayManage && g.status !== "active" && (
                                    <button className="icon-btn" onClick={() => void setStatus(g.id, "active")}>Reactivate</button>
                                )}
                            </div>
                            {openGoal === g.id && (
                                <div style={{ margin: "0.5rem 0 0 2.2rem" }}>
                                    {mayManage && (
                                        <div style={{ display: "flex", gap: "0.4rem", alignItems: "end", marginBottom: "0.5rem", flexWrap: "wrap" }}>
                                            <label style={{ fontSize: "0.8rem", flex: "1 1 240px" }}>Progress<br />
                                                <input style={{ width: "100%" }} value={note} placeholder="What happened this shift?"
                                                    onChange={(e) => setNote(e.target.value)} /></label>
                                            <select value={rating} onChange={(e) => setRating(e.target.value)}>
                                                <option value="on_track">On track</option>
                                                <option value="at_risk">At risk</option>
                                                <option value="achieved">Achieved</option>
                                            </select>
                                            <button className="btn small" disabled={!note.trim()} onClick={() => void addProgress()}>Add</button>
                                        </div>
                                    )}
                                    {(diary.data ?? []).map((p) => (
                                        <div key={p.id} style={{ fontSize: "0.83rem", padding: "0.25rem 0" }}>
                                            <span className="mono">{p.progress_date.slice(0, 10)}</span>
                                            {p.rating && <span className={`chip ${RATING_CHIP[p.rating] ?? "info"}`} style={{ margin: "0 6px" }}>
                                                {p.rating.replace(/_/g, " ")}</span>}
                                            {p.note}
                                            {p.author_name && <span style={{ color: "var(--muted)" }}> — {p.author_name}</span>}
                                        </div>
                                    ))}
                                    {(diary.data ?? []).length === 0 && (
                                        <div className="empty">No progress recorded yet.</div>
                                    )}
                                </div>
                            )}
                        </div>
                    ))}
            </div>
        </div>
    );
};

interface BurndownPeriod {
    funding_id: string; source: string; managed_by: string | null; is_active: boolean;
    period_start: string | null; period_end: string | null;
    budget_total: number; invoiced_spend: number; external_spend: number;
    total_burn: number; remaining: number; percent_used: number;
    projected_exhaustion: string | null; exhausts_before_period_end: boolean;
    categories: Array<{ id: string; category: string; allocated: string; spent: string }>;
}

/** The budget burn-down (096): computed from invoiced shifts + recorded
 *  external spend against the 062 funding record. Nothing here is typed in. */
const BurndownCard = ({ participantId }: { participantId: string }) => {
    const burndown = useResource<{ periods: BurndownPeriod[] }>(
        `/participant-budgets?participantId=${participantId}`);
    const periods = burndown.data?.periods ?? [];
    return (
        <div className="card">
            <div className="card-head"><h2>Budget burn-down</h2></div>
            <div className="card-body">
                {periods.length === 0
                    ? <div className="empty">No funding record on file — add one on the Funding tab and the burn-down computes itself.</div>
                    : periods.map((p) => (
                        <div key={p.funding_id} style={{ borderTop: "1px solid var(--line-soft)", padding: "0.6rem 0" }}>
                            <div style={{ display: "flex", gap: "0.6rem", alignItems: "center", flexWrap: "wrap" }}>
                                <strong>{p.source}</strong>
                                {p.managed_by && <span className="chip mute">{p.managed_by.replace(/_/g, " ")}</span>}
                                <span style={{ fontSize: "0.8rem", color: "var(--muted)" }}>
                                    {p.period_start?.slice(0, 10) ?? "…"} → {p.period_end?.slice(0, 10) ?? "…"}
                                </span>
                                <span className={`chip ${p.percent_used >= 90 ? "block" : p.percent_used >= 75 ? "warn" : "pass"}`}>
                                    {p.percent_used}% used
                                </span>
                                {p.exhausts_before_period_end && (
                                    <span className="chip block" title="At the current burn rate the budget runs out before the plan period ends.">
                                        projected to exhaust {p.projected_exhaustion}
                                    </span>
                                )}
                            </div>
                            <div style={{ background: "var(--line-soft)", borderRadius: 6, height: 10, margin: "0.5rem 0", overflow: "hidden" }}>
                                <div style={{ width: `${Math.min(100, p.percent_used)}%`, height: "100%",
                                    background: p.percent_used >= 90 ? "#b3261e" : p.percent_used >= 75 ? "#C78600" : "#288476" }} />
                            </div>
                            <div style={{ display: "flex", gap: "1.4rem", flexWrap: "wrap", fontSize: "0.84rem" }}>
                                <span><b>${p.budget_total.toLocaleString()}</b> budget</span>
                                <span><b>${p.invoiced_spend.toLocaleString()}</b> delivered by us (from invoices)</span>
                                <span><b>${p.external_spend.toLocaleString()}</b> recorded external spend</span>
                                <span><b>${p.remaining.toLocaleString()}</b> remaining</span>
                            </div>
                            {p.categories.length > 0 && (
                                <table style={{ marginTop: "0.5rem", fontSize: "0.83rem" }}>
                                    <thead><tr><th style={{ textAlign: "left" }}>Category</th><th>Allocated</th><th>External spend</th></tr></thead>
                                    <tbody>
                                        {p.categories.map((c) => (
                                            <tr key={c.id}>
                                                <td>{c.category}</td>
                                                <td style={{ textAlign: "center" }}>${Number(c.allocated).toLocaleString()}</td>
                                                <td style={{ textAlign: "center" }}>${Number(c.spent).toLocaleString()}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    ))}
            </div>
        </div>
    );
};

const RecordsCard = ({ participantId, mayManage }: { participantId: string; mayManage: boolean }) => {
    const records = useResource<ParticipantRecordRow[]>(`/participant-records?participantId=${participantId}`);
    const types = useResource<RecordTypeRow[]>("/participant-record-types");
    const [form, setForm] = useState({ typeId: "", issuedOn: "", expiresOn: "", notes: "" });
    const [file, setFile] = useState<File | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);

    const record = async () => {
        if (!form.typeId) return;
        setBusy(true);
        setError(null);
        try {
            let fileId: string | undefined;
            if (file) fileId = (await uploadFile(file, "general")).id;
            await api.post("/participant-records", {
                participantId,
                typeId: form.typeId,
                ...(fileId ? { fileId } : {}),
                ...(form.issuedOn ? { issuedOn: form.issuedOn } : {}),
                ...(form.expiresOn ? { expiresOn: form.expiresOn } : {}),
                ...(form.notes.trim() ? { notes: form.notes.trim() } : {}),
            });
            setForm({ typeId: "", issuedOn: "", expiresOn: "", notes: "" });
            setFile(null);
            records.reload();
        } catch (err) {
            setError(err instanceof Error ? err.message : "Could not record the document");
        } finally {
            setBusy(false);
        }
    };

    const acknowledge = async (id: string) => {
        setError(null);
        try {
            await api.post(`/participant-records/${id}/acknowledge`, {});
            records.reload();
        } catch (err) {
            setError(err instanceof Error ? err.message : "Could not acknowledge");
        }
    };

    return (
        <div className="card">
            <div className="card-head">
                <div>
                    <h2>Records</h2>
                    <p style={{ fontSize: "0.78rem", color: "var(--muted)", marginTop: "0.2rem" }}>
                        Plans, consents and agreements with their expiry dates. One current record
                        per type — recording a new one replaces the old. Acknowledge to sign that
                        you have read it.
                    </p>
                </div>
            </div>
            <div className="card-body">
                {mayManage && (
                    <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", alignItems: "end", marginBottom: "0.7rem" }}>
                        <label style={{ fontSize: "0.8rem" }}>Record type<br />
                            <select value={form.typeId} onChange={(e) => setForm({ ...form, typeId: e.target.value })}>
                                <option value="">Choose…</option>
                                {(types.data ?? []).map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                            </select></label>
                        <label style={{ fontSize: "0.8rem" }}>Issued<br />
                            <input type="date" value={form.issuedOn}
                                onChange={(e) => setForm({ ...form, issuedOn: e.target.value })} /></label>
                        <label style={{ fontSize: "0.8rem" }}>Expires<br />
                            <input type="date" value={form.expiresOn}
                                onChange={(e) => setForm({ ...form, expiresOn: e.target.value })} /></label>
                        <label style={{ fontSize: "0.8rem" }}>Document<br />
                            <input type="file" onChange={(e) => setFile(e.target.files?.[0] ?? null)} /></label>
                        <label style={{ fontSize: "0.8rem" }}>Notes<br />
                            <input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></label>
                        <button className="btn small" disabled={busy || !form.typeId} onClick={() => void record()}>
                            {busy ? "Recording…" : "Record"}
                        </button>
                    </div>
                )}
                {error && <div className="error-banner" style={{ marginBottom: "0.6rem" }}>{error}</div>}
                {(records.data ?? []).length === 0 ? (
                    <div className="empty">No records yet.</div>
                ) : (
                    <div className="table-wrap">
                        <table>
                            <thead><tr>
                                <th style={{ textAlign: "left" }}>Record</th>
                                <th>Issued</th><th>Expires</th><th>Status</th>
                                <th style={{ textAlign: "left" }}>Document</th>
                                <th>Read by</th><th></th>
                            </tr></thead>
                            <tbody>
                                {(records.data ?? []).map((r) => (
                                    <tr key={r.id}>
                                        <td>{r.type_name}{r.notes
                                            ? <span className="muted" style={{ display: "block", fontSize: "0.75rem" }}>{r.notes}</span> : null}</td>
                                        <td style={{ textAlign: "center" }}>{formatDate(r.issued_on)}</td>
                                        <td style={{ textAlign: "center" }}>{r.expires_on ? formatDate(r.expires_on) : "—"}</td>
                                        <td style={{ textAlign: "center" }}>
                                            <span className={`chip ${RECORD_CHIP[r.status]}`}>
                                                {r.status === "none" ? "no expiry" : r.status}
                                            </span>
                                        </td>
                                        <td>{r.file_id
                                            ? <a href={fileDownloadUrl(r.file_id)} target="_blank" rel="noreferrer">{r.file_name ?? "Download"}</a>
                                            : <span className="muted">—</span>}</td>
                                        <td style={{ textAlign: "center" }}>{r.ack_count}</td>
                                        <td style={{ textAlign: "center" }}>
                                            {r.acknowledged_by_me
                                                ? <span className="chip pass">Acknowledged</span>
                                                : <button className="icon-btn" onClick={() => void acknowledge(r.id)}>Acknowledge</button>}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

export const FundingCard = ({ participantId, canManage }: { participantId: string; canManage: boolean }) => {
    const funding = useResource<FundingRow[]>(`/participant-funding?participant_id=${participantId}`);
    const summary = useResource<Array<{ funding_id: string; allocated: string; delivered: string }>>(
        `/participants/${participantId}/funding-summary`);
    const [editing, setEditing] = useState<FundingRow | "new" | null>(null);

    const utilisation = new Map((summary.data ?? []).map((s) => [s.funding_id, s]));
    const dollars = (v: string | number | null) =>
        v == null ? "—" : `$${Number(v).toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

    const fields: Field[] = [
        { name: "source", label: "Funding source", kind: "select", half: true, options: [
            { value: "NDIS", label: "NDIS" },
            { value: "Self", label: "Self" },
            { value: "Other", label: "Other" },
        ] },
        { name: "managed_by", label: "Funding management", kind: "select", half: true, options: [
            { value: "self_managed", label: "Self managed" },
            { value: "plan_managed", label: "Plan managed" },
            { value: "ndia_managed", label: "NDIA managed" },
        ] },
        { name: "catalogue_version", label: "Catalogue version", kind: "select", half: true, options: [
            "2026-27", "2025-26", "2024-25", "2023-24",
        ].map((v) => ({ value: v, label: `NDIS Support Catalogue ${v}` })) },
        { name: "total", label: "Total amount ($)", kind: "number", half: true, min: 0 },
        { name: "start_date", label: "Funding start", kind: "date", half: true },
        { name: "end_date", label: "Funding end", kind: "date", half: true },
        { name: "review_date", label: "Review date", kind: "date", half: true },
        { name: "progress_date", label: "Progress date", kind: "date", half: true },
        { name: "referred_date", label: "Referred date", kind: "date", half: true },
        { name: "signed_date", label: "Signed date", kind: "date", half: true },
        { name: "notes", label: "Notes", kind: "textarea" },
    ];

    return (
        <div className="card" style={{ marginTop: "0.9rem" }}>
            <div className="card-head">
                <h2>Funding records</h2>
                {canManage && (
                    <button className="btn small" onClick={() => setEditing("new")}>+ Add funding</button>
                )}
            </div>
            <div className="card-body flush">
                <div className="table-wrap">
                    <table>
                        <thead>
                            <tr>
                                <th>Source</th><th>Managed by</th><th>Period</th><th>Total</th>
                                <th>Allocated</th><th>Delivered</th><th>Unallocated</th>
                                <th>Review</th><th>Status</th>{canManage && <th />}
                            </tr>
                        </thead>
                        <tbody>
                            {(funding.data ?? []).map((f) => {
                                const use = utilisation.get(f.id);
                                const unallocated = f.total != null && use
                                    ? Number(f.total) - Number(use.allocated) : null;
                                return (
                                    <tr key={f.id}>
                                        <td style={{ fontWeight: 600 }}>{f.source}{f.catalogue_version ? ` · ${f.catalogue_version}` : ""}</td>
                                        <td>{f.managed_by ? titleCase(f.managed_by) : "—"}</td>
                                        <td className="mono">
                                            {f.start_date ? `${formatDate(f.start_date)} – ${f.end_date ? formatDate(f.end_date) : "ongoing"}` : "—"}
                                        </td>
                                        <td className="mono">{dollars(f.total)}</td>
                                        <td className="mono">{dollars(use?.allocated ?? null)}</td>
                                        <td className="mono">{dollars(use?.delivered ?? null)}</td>
                                        <td className="mono" style={unallocated != null && unallocated < 0 ? { color: "var(--danger, #c23b3b)", fontWeight: 700 } : undefined}>
                                            {dollars(unallocated)}
                                        </td>
                                        <td className="mono">{f.review_date ? formatDate(f.review_date) : "—"}</td>
                                        <td>{f.is_active ? <span className="chip pass">Active</span> : <span className="chip mute">Ended</span>}</td>
                                        {canManage && (
                                            <td><button className="icon-btn" onClick={() => setEditing(f)}>Edit</button></td>
                                        )}
                                    </tr>
                                );
                            })}
                            {(funding.data ?? []).length === 0 && (
                                <tr><td colSpan={canManage ? 10 : 9} className="empty">No funding recorded yet.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
            {editing && (
                <EntityForm
                    title={editing === "new" ? "Add funding" : "Edit funding"}
                    path="/participant-funding" fields={fields}
                    initial={editing === "new" ? undefined : editing}
                    fixed={{ participant_id: participantId }}
                    onClose={() => setEditing(null)}
                    onSaved={() => { setEditing(null); funding.reload(); summary.reload(); }} />
            )}
        </div>
    );
};

const PARTICIPANT_STATUS = [
    { value: "active", label: "Active" },
    { value: "on_hold", label: "On hold" },
    { value: "inactive", label: "Inactive" },
    { value: "discharged", label: "Discharged" },
];

const SUPPORTS = ["Core", "COS", "Allied Health", "Capacity Building", "SIL", "Complex nursing"]
    .map((s) => ({ value: s, label: s }));

const participantFields: Field[] = [
    { name: "first_name", label: "First name", kind: "text", required: true, half: true },
    { name: "last_name", label: "Last name", kind: "text", required: true, half: true },
    { name: "ndis_number", label: "NDIS number", kind: "text", half: true },
    { name: "date_of_birth", label: "Date of birth", kind: "date", half: true },
    { name: "primary_phone", label: "Phone", kind: "tel", half: true },
    { name: "primary_email", label: "Email", kind: "email", half: true },
    { name: "status", label: "Status", kind: "select", options: PARTICIPANT_STATUS, half: true },
    { name: "region", label: "Region", kind: "text", half: true },
    { name: "address", label: "Address", kind: "text", half: true },
    { name: "suburb", label: "Suburb", kind: "text", half: true },
    { name: "postcode", label: "Postcode", kind: "text", half: true },
    { name: "primary_contact", label: "Primary contact", kind: "text", half: true },
    { name: "primary_disability", label: "Primary disability", kind: "select", half: true,
      optionsFrom: { path: "/disability-types", addLabel: "Add disability" } },
    { name: "secondary_disability", label: "Secondary disability", kind: "select", half: true,
      optionsFrom: { path: "/disability-types", addLabel: "Add disability" } },
    { name: "plan_managed_by", label: "Plan managed by", kind: "select", half: true, options: [
        { value: "self_managed", label: "Self managed" },
        { value: "plan_managed", label: "Plan managed" },
        { value: "ndia_managed", label: "NDIA managed" },
    ] },
    { name: "gender", label: "Gender", kind: "select", half: true, options: [
        { value: "female", label: "Female" },
        { value: "male", label: "Male" },
        { value: "non_binary", label: "Non-binary" },
        { value: "prefer_not_to_say", label: "Prefer not to say" },
        { value: "other", label: "Other" },
    ] },
    { name: "timezone", label: "Timezone", kind: "select", half: true, options: [
        "Australia/Perth", "Australia/Adelaide", "Australia/Brisbane", "Australia/Broken_Hill",
        "Australia/Darwin", "Australia/Eucla", "Australia/Hobart", "Australia/Lindeman",
        "Australia/Lord_Howe", "Australia/Melbourne", "Australia/Sydney",
    ].map((tz) => ({ value: tz, label: tz })) },
    { name: "quick_snapshot", label: "Quick snapshot", kind: "textarea",
      help: "One paragraph a worker reads first — who this person is at a glance." },
    { name: "public_guardian", label: "Public guardian", kind: "text", half: true },
    { name: "plan_nominee", label: "Plan nominee", kind: "text", half: true },
    { name: "xero_invoice_reference", label: "Invoice reference (Xero)", kind: "text", half: true,
      help: "Used as the Xero invoice reference. Leave empty to use the NDIS number." },
    { name: "conditions", label: "Participant conditions", kind: "multiselect",
      optionsFrom: { path: "/participant-conditions", addLabel: "Add condition" },
      help: "Clinical attributes staff must know — drives safe rostering. Managed in Settings." },
    { name: "plan_start_date", label: "Plan start", kind: "date", half: true },
    { name: "plan_end_date", label: "Plan end", kind: "date", half: true },
    { name: "service_start_date", label: "Service start", kind: "date", half: true },
    { name: "service_end_date", label: "Service end", kind: "date", half: true },
    { name: "external_coordinator_name", label: "External coordinator — name", kind: "text", half: true },
    { name: "external_coordinator_company", label: "External coordinator — company", kind: "text", half: true },
    { name: "external_coordinator_email", label: "External coordinator — email", kind: "email", half: true },
    { name: "external_coordinator_phone", label: "External coordinator — phone", kind: "tel", half: true },
    { name: "important_alert", label: "Important alert", kind: "text",
      help: "One line shown prominently on the profile — e.g. severe nut allergy. Max 256 characters." },
    { name: "shift_instructions", label: "Shift instructions", kind: "textarea",
      help: "Standing instructions every rostered worker should follow at this participant's shifts." },
    { name: "pricing_state", label: "Service catalogue state", kind: "select", half: true,
      options: ["ACT", "NSW", "NT", "QLD", "SA", "TAS", "VIC", "WA"].map((s) => ({ value: s, label: s })) },
    { name: "pricing_category", label: "Pricing category", kind: "select", half: true, options: [
        { value: "standard", label: "Standard" },
        { value: "high_intensity", label: "High intensity" },
        { value: "standard_ttp", label: "Standard TTP" },
        { value: "high_intensity_ttp", label: "High-intensity TTP" },
    ] },
    { name: "invoice_emails", label: "Invoice emails", kind: "text", half: true,
      placeholder: "billing@provider.com.au, pm@planmanager.com.au",
      help: "Comma-separated — who receives this participant's invoices." },
    { name: "audit_opt_in", label: "Include in audit reports and compliance checks", kind: "checkbox" },
    { name: "support_coordination", label: "Requires support coordination services", kind: "checkbox" },
    { name: "allied_health", label: "Requires allied health services", kind: "checkbox" },
    { name: "service_supports", label: "Service supports", kind: "multiselect", options: SUPPORTS },
    { name: "next_of_kin_name", label: "Next of kin — name", kind: "text", half: true },
    { name: "next_of_kin_relationship", label: "Next of kin — relationship", kind: "text", half: true,
      placeholder: "e.g. mother, guardian, spouse" },
    { name: "next_of_kin_phone", label: "Next of kin — phone", kind: "tel", half: true },
    { name: "household_pets", label: "Pets at the home", kind: "text", half: true,
      placeholder: "e.g. large dog — kept outside during visits",
      help: "Staff see this before visiting — animal type and any handling arrangement." },
    { name: "behaviour_notes", label: "Behaviour notes", kind: "textarea",
      help: "How the participant may present, known triggers, and the approach that works. Staff-visible." },
    { name: "access_notes", label: "Home access notes", kind: "textarea",
      help: "Entry instructions, key safe, parking, who else may be home. Staff-visible." },
    { name: "notes", label: "Notes", kind: "textarea" },
];

/** The static fields plus the provider link, which needs the companies list. */
const participantFieldsWith = (companyOptions: Option[], employeeOptions: Option[]): Field[] => [
    ...participantFields,
    { name: "company_id", label: "Provider organisation", kind: "select",
      options: companyOptions,
      addNew: { title: "New organisation", path: "/companies", fields: companyCreateFields() },
      help: "The partner organisation this participant belongs to — it decides which partner portal can see them." },
    { name: "coordinator_employee_id", label: "Coordinator", kind: "searchselect", half: true,
      options: employeeOptions,
      help: "The TrustCare staff member who coordinates this participant's care." },
];

const MED_ROUTE = ["oral", "topical", "subcutaneous", "intramuscular", "intravenous", "peg", "inhaled", "other"]
    .map((r) => ({ value: r, label: titleCase(r) }));
const MED_STATUS = ["active", "ceased", "on_hold", "pending_review"]
    .map((r) => ({ value: r, label: titleCase(r) }));
const NOTE_KIND = ["progress", "clinical", "wound_care", "medication", "behaviour", "incident_follow_up", "other"]
    .map((r) => ({ value: r, label: titleCase(r) }));

type Dialog =
    | { kind: "create" }
    | { kind: "edit"; participant: Participant }
    | { kind: "delete"; participant: Participant }
    | { kind: "assign"; participant: Participant }
    | { kind: "unassign"; assignmentId: string; name: string; excluded: boolean }
    | { kind: "medication"; participant: Participant; existing?: Medication }
    | { kind: "note"; participant: Participant }
    | { kind: "handover"; participant: Participant }
    | { kind: "acknowledge"; handover: Handover }
    | { kind: "document"; participant: Participant; docKind: string }
    | null;

export const Participants = () => {
    const { user, mayQuality } = useAuth();
    const canManage = user?.role === "admin" || user?.role === "manager";
    // Support workers write clinical records for the people they support — that
    // is what a service note is for. Only the participant record itself, and the
    // worker assignments that feed the compliance rules, are manager-only.
    const canRecordClinical = user?.role !== "viewer";

    const participants = useResource<Participant[]>("/participants");
    const companies = useResource<Array<{ id: string; name: string }>>(canManage ? "/companies" : null);
    const assignments = useResource<Array<Assignment & { id: string }>>("/assignments");
    const employees = useResource<Employee[]>("/employees");
    const shifts = useResource<Shift[]>("/shifts");
    const medications = useResource<Medication[]>("/medications");
    const notes = useResource<ServiceNote[]>("/service-notes");
    const handovers = useResource<Handover[]>("/handovers");
    const formTemplates = useResource<FormTemplate[]>("/form-templates");
    const formSubmissions = useResource<FormSubmission[]>("/form-submissions");
    const [fillingTemplate, setFillingTemplate] = useState<FormTemplate | null>(null);
    // Directory rollups (prototype parity): budgets + incidents for the tiles
    // and per-row chips. Funding is supervisor+ (RLS); staff simply skip it.
    const canSeeFunding = canManage || user?.role === "supervisor";
    const allBudgets = useResource<Array<{ participant_id: string; allocated: string; spent: string }>>(
        canSeeFunding ? "/participant-budgets" : null);
    const allIncidents = useResource<Array<{ id: string; participant_id: string | null; status: string }>>(
        "/incidents");
    // Provider-submitted participant registrations awaiting review (060).
    const registrations = useResource<Array<{
        id: string; first_name: string; last_name: string; ndis_number: string | null;
        date_of_birth: string | null; phone: string | null; suburb: string | null;
        services: string[]; notes: string | null; company_name: string; created_at: string;
    }>>(canManage ? "/participant-registrations?status=pending" : null);

    const [selected, setSelected] = useState<Participant | null>(null);
    const [tab, setTab] = useState<Tab>("overview");
    const [regBusyId, setRegBusyId] = useState<string | null>(null);
    const [regError, setRegError] = useState<string | null>(null);

    const decideRegistration = async (id: string, action: "approve" | "reject", reason?: string) => {
        setRegBusyId(id);
        setRegError(null);
        try {
            await api.post(`/participant-registrations/${id}/${action}`, reason ? { reason } : {});
            registrations.reload();
            participants.reload();
        } catch (err) {
            setRegError(err instanceof Error ? err.message : "Could not decide the registration");
        } finally {
            setRegBusyId(null);
        }
    };
    const [medError, setMedError] = useState<string | null>(null);
    const verifyMedication = async (id: string) => {
        setMedError(null);
        try {
            await api.post(`/medications/${id}/verify`, {});
            medications.reload();
        } catch (err) {
            setMedError(err instanceof Error ? err.message : "Could not verify the medication");
        }
    };
    const participantDocs = useResource<ParticipantDoc[]>(
        selected ? `/partner-documents?participantId=${selected.id}` : null);
    const shiftCarePlans = useResource<ShiftCarePlan[]>(
        selected ? `/participants/${selected.id}/care-plans` : null);
    const participantInvoices = useResource<ParticipantInvoice[]>(
        selected && canManage ? `/participants/${selected.id}/invoices` : null);
    const [search, setSearch] = useState("");
    const [filters, setFilters] = useState<Record<string, string>>(() => {
        const params = new URLSearchParams(window.location.search);
        const provider = params.get("provider");
        return (provider ? { provider } : {}) as Record<string, string>;
    });
    const [dialog, setDialog] = useState<Dialog>(null);

    // Deep link: /participants?id=… lands with that person open — how an
    // invoice or roster row can point straight at a participant record.
    useEffect(() => {
        const wanted = new URLSearchParams(window.location.search).get("id");
        if (!wanted || selected) return;
        const hit = (participants.data ?? []).find((row) => row.id === wanted);
        if (hit) setSelected(hit);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [participants.data]);

    const refresh = () => {
        participants.reload();
        assignments.reload();
        medications.reload();
        notes.reload();
        handovers.reload();
        setDialog(null);
    };

    const employeeName = useMemo(() => {
        const map = new Map((employees.data ?? []).map((e) => [e.id, e.full_name]));
        return (id: string | null) => (id ? map.get(id) ?? "Unknown" : "—");
    }, [employees.data]);

    const workersFor = (participantId: string) =>
        (assignments.data ?? []).filter((a) => a.participant_id === participantId);

    const employeeOptions = (employees.data ?? []).map((e) => ({ value: e.id, label: e.full_name }));

    const medicationFields: Field[] = [
        { name: "name", label: "Medication", kind: "text", required: true, half: true },
        { name: "dose", label: "Dose", kind: "text", required: true, half: true },
        { name: "frequency", label: "Frequency", kind: "text", required: true, half: true, placeholder: "e.g. Twice daily" },
        { name: "route", label: "Route", kind: "select", options: MED_ROUTE, half: true },
        { name: "status", label: "Status", kind: "select", options: MED_STATUS, half: true },
        { name: "prescriber", label: "Prescriber", kind: "text", half: true },
        { name: "prescribed_on", label: "Prescribed on", kind: "date", half: true },
        { name: "started_on", label: "Started on", kind: "date", half: true },
        { name: "ceased_on", label: "Ceased on", kind: "date", half: true,
          help: "Required when the status is Ceased." },
        { name: "ceased_reason", label: "Reason ceased", kind: "text", half: true },
        { name: "is_prn", label: "PRN (as required)", kind: "checkbox" },
        { name: "prn_instructions", label: "PRN instructions", kind: "textarea",
          help: "Required when PRN is ticked — the database refuses the record without it." },
        { name: "notes", label: "Notes", kind: "textarea" },
    ];

    const noteFields: Field[] = [
        { name: "kind", label: "Note type", kind: "select", options: NOTE_KIND, half: true },
        { name: "note_date", label: "Date", kind: "date", required: true, half: true },
        { name: "employee_id", label: "Worker", kind: "select", options: employeeOptions, half: true },
        { name: "subject", label: "Subject", kind: "text", required: true },
        { name: "body", label: "Note", kind: "textarea", required: true },
        { name: "follow_up", label: "Follow-up required", kind: "textarea" },
    ];

    const handoverFields: Field[] = [
        { name: "from_employee_id", label: "Handing over", kind: "select", options: employeeOptions, half: true },
        { name: "to_employee_id", label: "Receiving", kind: "select", options: employeeOptions, half: true },
        { name: "occurred_at", label: "Time of handover", kind: "datetime", half: true },
        { name: "status", label: "Status", kind: "select", half: true,
          options: [{ value: "draft", label: "Draft" }, { value: "issued", label: "Issued" }],
          help: "Issued handovers stay on the dashboard until someone acknowledges them." },
        { name: "summary", label: "Summary", kind: "textarea", required: true },
        { name: "clinical_changes", label: "Clinical changes", kind: "textarea" },
        { name: "risks", label: "Risks", kind: "textarea" },
        { name: "actions_required", label: "Actions required", kind: "textarea" },
    ];

    const assignFields: Field[] = [
        { name: "employee_id", label: "Worker", kind: "select", required: true, options: employeeOptions },
        { name: "assignment_type", label: "Assignment", kind: "select", required: true, options: [
            { value: "preferred", label: "Preferred — roster them first" },
            { value: "approved", label: "Approved — may be rostered" },
            { value: "excluded", label: "Excluded — must never be rostered" },
        ] },
        { name: "reason", label: "Reason", kind: "textarea",
          help: "An exclusion cannot be overridden by any role, so record why it was made." },
    ];

    const dialogs = (
        <>
            {dialog?.kind === "create" && (
                <EntityForm title="Add New Participant" path="/participants" fields={participantFieldsWith((companies.data ?? []).map((c) => ({ value: c.id, label: c.name })), (employees.data ?? []).map((e) => ({ value: e.id, label: e.full_name })))}
                    onClose={() => setDialog(null)} onSaved={refresh} />
            )}
            {dialog?.kind === "edit" && (
                <EntityForm title={`Edit ${dialog.participant.full_name}`} path="/participants"
                    fields={participantFieldsWith((companies.data ?? []).map((c) => ({ value: c.id, label: c.name })), (employees.data ?? []).map((e) => ({ value: e.id, label: e.full_name })))} initial={dialog.participant}
                    onClose={() => setDialog(null)} onSaved={refresh} />
            )}
            {dialog?.kind === "assign" && (
                <EntityForm title={`Assign a worker to ${dialog.participant.full_name}`} path="/assignments"
                    fields={assignFields} fixed={{ participant_id: dialog.participant.id }}
                    onClose={() => setDialog(null)} onSaved={refresh} />
            )}
            {dialog?.kind === "medication" && (
                <EntityForm
                    title={dialog.existing ? `Edit ${dialog.existing.name}` : `Add medication — ${dialog.participant.full_name}`}
                    path="/medications" fields={medicationFields} initial={dialog.existing}
                    fixed={{ participant_id: dialog.participant.id }}
                    onClose={() => setDialog(null)} onSaved={refresh} />
            )}
            {dialog?.kind === "note" && (
                <EntityForm title={`Add service note — ${dialog.participant.full_name}`} path="/service-notes"
                    fields={noteFields} fixed={{ participant_id: dialog.participant.id }}
                    subtitle="A service note is a clinical record. Corrections are made by writing a further note, not by editing this one."
                    onClose={() => setDialog(null)} onSaved={refresh} />
            )}
            {dialog?.kind === "handover" && (
                <EntityForm title={`Create handover — ${dialog.participant.full_name}`} path="/handovers"
                    fields={handoverFields} fixed={{ participant_id: dialog.participant.id }}
                    onClose={() => setDialog(null)} onSaved={refresh} />
            )}
            {dialog?.kind === "acknowledge" && (
                <ConfirmDialog
                    title="Acknowledge this handover?"
                    message="You are confirming you have read it and accept the actions it lists."
                    warning="Your name and the time are recorded against it permanently."
                    confirmLabel="Acknowledge"
                    request={{ method: "post", path: `/handovers/${dialog.handover.id}/acknowledge` }}
                    onClose={() => setDialog(null)} onDone={refresh} />
            )}
            {dialog?.kind === "unassign" && (
                <ConfirmDialog
                    title={`Remove ${dialog.name} from this participant?`}
                    tone="danger"
                    message="They will no longer be listed as an assigned worker."
                    warning={dialog.excluded
                        ? "This is an EXCLUSION. Removing it means the roster will stop refusing to schedule this worker for this participant. Make sure that is what you intend."
                        : "Existing shifts are not affected."}
                    confirmLabel="Remove assignment"
                    request={{ method: "delete", path: `/assignments/${dialog.assignmentId}` }}
                    onClose={() => setDialog(null)} onDone={refresh} />
            )}
            {dialog?.kind === "delete" && (
                <ConfirmDialog
                    title={`Discharge ${dialog.participant.full_name}?`}
                    tone="danger"
                    message="They are retired from active lists and can no longer be rostered."
                    warning="Shift history, service notes, medications, handovers and incidents all remain in place — NDIS record keeping runs to seven years. Nothing here is erased."
                    confirmLabel="Discharge participant"
                    request={{ method: "delete", path: `/participants/${dialog.participant.id}` }}
                    onClose={() => setDialog(null)}
                    onDone={() => { refresh(); setSelected(null); }} />
            )}
        </>
    );

    if (participants.error) return <div className="error-banner">{participants.error}</div>;

    if (selected) {
        const workers = workersFor(selected.id);
        const roster = (shifts.data ?? [])
            .filter((s) => s.participant_id === selected.id && s.status !== "cancelled")
            .sort((a, b) => a.starts_at.localeCompare(b.starts_at));
        const meds = (medications.data ?? []).filter((m) => m.participant_id === selected.id);
        const theirNotes = (notes.data ?? []).filter((n) => n.participant_id === selected.id);
        const theirHandovers = (handovers.data ?? []).filter((h) => h.participant_id === selected.id);

        const docs = participantDocs.data ?? [];
        const generalDocs = docs.filter((d) => d.kind !== "risk_assessment");
        const riskDocs = docs.filter((d) => d.kind === "risk_assessment");
        const theirForms = (formSubmissions.data ?? [])
            .filter((s) => s.participant_id === selected.id)
            .sort((a, b) => b.created_at.localeCompare(a.created_at));
        const publishedTemplates = (formTemplates.data ?? []).filter((t) => t.status === "published");
        const templateById = new Map((formTemplates.data ?? []).map((t) => [t.id, t]));

        const TABS: Array<[Tab, string]> = [
            ["overview", "Overview"],
            ["workers", "Assigned workers"],
            ["roster", "Roster"],
            ["medications", `Medications (${meds.filter((m) => m.status === "active").length})`],
            ["notes", `Service notes (${theirNotes.length})`],
            ["handovers", `Handovers (${theirHandovers.length})`],
            ["documents", `Documents (${generalDocs.length + (shiftCarePlans.data ?? []).length})`],
            ["records", "Records"],
            ...(mayQuality("participants.goals.view") || mayQuality("participants.goals.manage")
                ? [["goals", "Goals"]] as Array<[Tab, string]> : []),
            ...(mayQuality("participants.budgets.view")
                ? [["burndown", "Budget burn-down"]] as Array<[Tab, string]> : []),
            ["risk", `Risk assessment (${riskDocs.length})`],
            ["forms", `Forms (${theirForms.length})`],
            ["funding", "Funding"],
            ...(canManage ? [
                ["invoices", `Invoices (${(participantInvoices.data ?? []).length})`],
                ["settings", "Settings"],
            ] as Array<[Tab, string]> : []),
        ];

        return (
            <div>
                {dialogs}
                <div className="toolbar">
                    <button className="btn ghost small" onClick={() => setSelected(null)}>← All participants</button>
                    <div className="spacer" />
                    <span className={`chip ${selected.status === "active" ? "pass" : "mute"}`}>
                        {titleCase(selected.status)}
                    </span>
                    {canManage && (
                        <>
                            <button className="btn ghost small" onClick={() => setDialog({ kind: "edit", participant: selected })}>Edit</button>
                            <button className="btn ghost small" onClick={() => setDialog({ kind: "delete", participant: selected })}>Discharge</button>
                        </>
                    )}
                </div>

                <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", marginBottom: "1.1rem" }}>
                    <InitialsAvatar name={selected.full_name} size={40}
                        photoUrl={selected.photo_file_id ? fileViewUrl(selected.photo_file_id) : null} />
                    <div>
                        <h2 style={{ marginBottom: "0.1rem" }}>{selected.full_name}</h2>
                        <p style={{ color: "var(--muted)", fontSize: "0.83rem" }}>
                            {selected.ref_code && <span className="mono">{selected.ref_code} · </span>}
                            {selected.ndis_number ?? "No NDIS number"} · {selected.region ?? "No region"}
                        </p>
                    </div>
                </div>

                {selected.important_alert && (
                    <div className="error-banner" style={{ background: "#FCF3E3", borderColor: "#E0B75B", color: "#7A5B12" }}>
                        <b>Important:</b> {selected.important_alert}
                    </div>
                )}
                <div className="tabs">
                    {TABS.map(([value, label]) => (
                        <button key={value} className={tab === value ? "active" : ""} onClick={() => setTab(value)}>
                            {label}
                        </button>
                    ))}
                </div>

                {tab === "overview" && (
                    <div className="grid cols-2">
                        <div className="card">
                            <div className="card-head"><h2>Details</h2></div>
                            <div className="card-body">
                                {selected.quick_snapshot && (
                                    <p style={{ fontSize: "0.86rem", color: "var(--ink-soft)", lineHeight: 1.55,
                                        borderBottom: "1px solid var(--line-soft)", paddingBottom: "0.6rem", marginBottom: "0.6rem" }}>
                                        {selected.quick_snapshot}
                                    </p>
                                )}
                                <dl className="kv">
                                    <dt>Date of birth</dt><dd>{formatDate(selected.date_of_birth)}</dd>
                                    <dt>Address</dt><dd>{selected.address ?? "—"}</dd>
                                    <dt>Phone</dt><dd>{selected.primary_phone ?? "—"}</dd>
                                    <dt>Primary contact</dt><dd>{selected.primary_contact ?? "—"}</dd>
                                    <dt>Gender</dt><dd>{selected.gender ? titleCase(selected.gender) : "—"}</dd>
                                    <dt>Primary disability</dt><dd>{selected.primary_disability ?? "—"}</dd>
                                    <dt>Secondary disability</dt><dd>{selected.secondary_disability ?? "—"}</dd>
                                    <dt>Plan managed by</dt><dd>{selected.plan_managed_by ? titleCase(selected.plan_managed_by) : "—"}</dd>
                                    <dt>Public guardian</dt><dd>{selected.public_guardian ?? "—"}</dd>
                                    <dt>Plan nominee</dt><dd>{selected.plan_nominee ?? "—"}</dd>
                                    <dt>Conditions</dt>
                                    <dd>
                                        {(selected.conditions ?? []).length > 0
                                            ? (selected.conditions ?? []).map((c) => (
                                                <span key={c} className="chip warn" style={{ marginRight: 3 }}>{c}</span>))
                                            : "—"}
                                    </dd>
                                    <dt>External coordinator</dt>
                                    <dd>
                                        {selected.external_coordinator_name
                                            ? `${selected.external_coordinator_name}${selected.external_coordinator_company ? ` (${selected.external_coordinator_company})` : ""}${selected.external_coordinator_phone ? ` · ${selected.external_coordinator_phone}` : ""}${selected.external_coordinator_email ? ` · ${selected.external_coordinator_email}` : ""}`
                                            : "—"}
                                    </dd>
                                    <dt>Service period</dt>
                                    <dd>{selected.service_start_date ? `${formatDate(selected.service_start_date)} – ${selected.service_end_date ? formatDate(selected.service_end_date) : "ongoing"}` : "—"}</dd>
                                    <dt>Plan period</dt>
                                    <dd>{formatDate(selected.plan_start_date)} – {formatDate(selected.plan_end_date)}</dd>
                                    <dt>Supports</dt>
                                    <dd>
                                        {selected.service_supports.map((s) => (
                                            <span key={s} className="chip info" style={{ marginRight: 3 }}>{s}</span>
                                        ))}
                                    </dd>
                                </dl>
                            </div>
                        </div>

                        <div className="card">
                            <div className="card-head"><h2>Care &amp; safety</h2></div>
                            <div className="card-body">
                                <dl className="kv">
                                    <dt>Next of kin</dt><dd>
                                        {selected.next_of_kin_name
                                            ? `${selected.next_of_kin_name}${selected.next_of_kin_relationship ? ` (${selected.next_of_kin_relationship})` : ""}${selected.next_of_kin_phone ? ` · ${selected.next_of_kin_phone}` : ""}`
                                            : "—"}
                                    </dd>
                                    <dt>Pets at the home</dt><dd>
                                        {selected.household_pets
                                            ? <span className="chip warn">{selected.household_pets}</span>
                                            : "—"}
                                    </dd>
                                    <dt>Behaviour</dt><dd style={{ whiteSpace: "pre-wrap" }}>
                                        {selected.behaviour_notes ?? "—"}
                                    </dd>
                                    <dt>Home access</dt><dd style={{ whiteSpace: "pre-wrap" }}>
                                        {selected.access_notes ?? "—"}
                                    </dd>
                                    <dt>Shift instructions</dt><dd style={{ whiteSpace: "pre-wrap" }}>
                                        {selected.shift_instructions ?? "—"}
                                    </dd>
                                </dl>
                            </div>
                        </div>

                        <div className="card">
                            <div className="card-head"><h2>Goals, likes &amp; dislikes</h2></div>
                            <div className="card-body" style={{ display: "grid", gap: "0.85rem" }}>
                                {([["Goals", selected.goals, "info"], ["Likes", selected.likes, "pass"], ["Dislikes", selected.dislikes, "block"]] as const).map(
                                    ([label, values, chip]) => (
                                        <div key={label}>
                                            <div style={{ fontSize: "0.72rem", color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: "0.3rem" }}>
                                                {label}
                                            </div>
                                            {values.length === 0
                                                ? <span style={{ color: "var(--muted)", fontSize: "0.83rem" }}>None recorded</span>
                                                : values.map((v) => <span key={v} className={`chip ${chip}`} style={{ marginRight: 4 }}>{v}</span>)}
                                        </div>
                                    )
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {tab === "overview" && <ParticipantCareSummary participant={selected} />}
                {tab === "funding" && (
                    <FundingCard participantId={selected.id} canManage={canManage} />
                )}
                {tab === "records" && (
                    <RecordsCard participantId={selected.id}
                        mayManage={mayQuality("participants.records.manage")} />
                )}
                {tab === "goals" && (
                    <GoalsCard participantId={selected.id}
                        mayManage={mayQuality("participants.goals.manage")} />
                )}
                {tab === "burndown" && (
                    <BurndownCard participantId={selected.id} />
                )}

                {tab === "workers" && (
                    <div className="card">
                        <div className="card-head">
                            <h2>Assigned workers</h2>
                            {canManage && (
                                <button className="btn small" onClick={() => setDialog({ kind: "assign", participant: selected })}>
                                    + Assign worker
                                </button>
                            )}
                        </div>
                        <div className="card-body flush">
                            <div className="table-wrap">
                                <table>
                                    <thead>
                                        <tr>
                                            <th>Worker</th><th>Assignment</th><th>Reason</th>
                                            {canManage && <th style={{ textAlign: "right" }}>Actions</th>}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {workers.map((worker) => (
                                            <tr key={worker.id}>
                                                <td>
                                                    <div style={{ display: "flex", alignItems: "center", gap: "0.55rem", fontWeight: 600 }}>
                                                        <InitialsAvatar name={employeeName(worker.employee_id)} size={26} />
                                                        {employeeName(worker.employee_id)}
                                                    </div>
                                                </td>
                                                <td>
                                                    <span className={`chip ${worker.assignment_type === "excluded" ? "block" : worker.assignment_type === "preferred" ? "pass" : "info"}`}>
                                                        {worker.assignment_type}
                                                    </span>
                                                </td>
                                                <td style={{ color: "var(--ink-soft)" }}>{worker.reason ?? "—"}</td>
                                                {canManage && (
                                                    <td>
                                                        <div className="row-actions">
                                                            <button className="icon-btn danger" onClick={() => setDialog({
                                                                kind: "unassign", assignmentId: worker.id,
                                                                name: employeeName(worker.employee_id),
                                                                excluded: worker.assignment_type === "excluded",
                                                            })}>Remove</button>
                                                        </div>
                                                    </td>
                                                )}
                                            </tr>
                                        ))}
                                        {workers.length === 0 && (
                                            <tr><td colSpan={canManage ? 4 : 3} className="empty">No workers assigned.</td></tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                        <div className="card-body" style={{ borderTop: "1px solid var(--line-soft)", color: "var(--muted)", fontSize: "0.8rem" }}>
                            An <strong>excluded</strong> worker cannot be rostered to this participant by anyone — that
                            rule is not overridable, at any role.
                        </div>
                    </div>
                )}

                {tab === "roster" && (
                    <div className="card">
                        <div className="card-head"><h2>Roster</h2></div>
                        <div className="card-body flush">
                            <div className="table-wrap">
                                <table>
                                    <thead><tr><th>Date</th><th>Time</th><th>Worker</th><th>Role</th><th>Status</th></tr></thead>
                                    <tbody>
                                        {roster.map((shift) => (
                                            <tr key={shift.id}>
                                                <td className="mono">{formatDate(shift.starts_at)}</td>
                                                <td className="mono">{formatTime(shift.starts_at)}–{formatTime(shift.ends_at)}</td>
                                                <td>
                                                    {shift.employee_id ? (
                                                        <Link to={`/employees?id=${shift.employee_id}`}
                                                            style={{ display: "flex", alignItems: "center", gap: "0.55rem", textDecoration: "none", color: "inherit", fontWeight: 600 }}>
                                                            <InitialsAvatar name={shift.employee_name} size={24} />
                                                            {shift.employee_name}
                                                        </Link>
                                                    ) : (
                                                        <div style={{ display: "flex", alignItems: "center", gap: "0.55rem" }}>
                                                            <InitialsAvatar name={null} size={24} />
                                                            Unassigned
                                                        </div>
                                                    )}
                                                </td>
                                                <td>{shift.role_name ?? "—"}</td>
                                                <td>
                                                    <span className={`chip ${shift.override_reason ? "warn" : "pass"}`}>
                                                        {shift.override_reason ? "overridden" : shift.status}
                                                    </span>
                                                </td>
                                            </tr>
                                        ))}
                                        {roster.length === 0 && <tr><td colSpan={5} className="empty">No shifts scheduled.</td></tr>}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                )}

                {tab === "medications" && medError && (
                    <div className="error-banner">{medError}</div>
                )}
                {tab === "medications" && (
                    <div className="card">
                        <div className="card-head">
                            <h2>Medications</h2>
                            {canRecordClinical && (
                                <button className="btn small" onClick={() => setDialog({ kind: "medication", participant: selected })}>
                                    + Add medication
                                </button>
                            )}
                        </div>
                        <div className="card-body flush">
                            <div className="table-wrap">
                                <table>
                                    <thead>
                                        <tr>
                                            <th>Medication</th><th>Dose</th><th>Route</th><th>Frequency</th><th>Status</th>
                                            {canRecordClinical && <th style={{ textAlign: "right" }}>Actions</th>}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {meds.map((med) => (
                                            <tr key={med.id}>
                                                <td>
                                                    <div style={{ fontWeight: 600 }}>
                                                        {med.name}
                                                        {med.is_prn && <span className="chip warn" style={{ marginLeft: 5 }}>PRN</span>}
                                                    </div>
                                                    {med.prn_instructions && (
                                                        <div style={{ fontSize: "0.75rem", color: "var(--muted)", marginTop: 2 }}>
                                                            {med.prn_instructions}
                                                        </div>
                                                    )}
                                                </td>
                                                <td>{med.dose}</td>
                                                <td>{titleCase(med.route)}</td>
                                                <td>{med.frequency}</td>
                                                <td>
                                                    <span className={`chip ${med.status === "active" ? "pass" : med.status === "ceased" ? "mute" : "warn"}`}>
                                                        {titleCase(med.status)}
                                                    </span>
                                                    {med.added_by_company_id && (
                                                        med.verified_at
                                                            ? <span className="chip pass" style={{ marginLeft: 5 }} title="Provider-added entry, verified by TrustCare.">Verified</span>
                                                            : <span className="chip warn" style={{ marginLeft: 5 }} title="Added by the provider — check it against the chart and verify.">Provider-added · unverified</span>
                                                    )}
                                                    {med.ceased_on && (
                                                        <div style={{ fontSize: "0.72rem", color: "var(--muted)", marginTop: 2 }}>
                                                            {formatDate(med.ceased_on)} — {med.ceased_reason}
                                                        </div>
                                                    )}
                                                </td>
                                                {canRecordClinical && (
                                                    <td>
                                                        <div className="row-actions">
                                                            {med.added_by_company_id && !med.verified_at && (
                                                                <button className="icon-btn" title="Confirm this provider-added entry matches the participant's chart"
                                                                    onClick={() => void verifyMedication(med.id)}>
                                                                    Verify
                                                                </button>
                                                            )}
                                                            <button className="icon-btn" onClick={() => setDialog({ kind: "medication", participant: selected, existing: med })}>
                                                                Edit
                                                            </button>
                                                        </div>
                                                    </td>
                                                )}
                                            </tr>
                                        ))}
                                        {meds.length === 0 && (
                                            <tr><td colSpan={canRecordClinical ? 6 : 5} className="empty">No medications recorded.</td></tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                )}

                {tab === "notes" && (
                    <div className="card">
                        <div className="card-head">
                            <h2>Service notes</h2>
                            {canRecordClinical && (
                                <button className="btn small" onClick={() => setDialog({ kind: "note", participant: selected })}>
                                    + Add note
                                </button>
                            )}
                        </div>
                        <div className="card-body" style={{ display: "grid", gap: "0.9rem" }}>
                            {theirNotes.map((note) => (
                                <div key={note.id} style={{ borderLeft: "3px solid var(--accent)", paddingLeft: "0.8rem" }}>
                                    <div style={{ display: "flex", gap: "0.5rem", alignItems: "center", flexWrap: "wrap" }}>
                                        <strong style={{ fontSize: "0.88rem" }}>{note.subject}</strong>
                                        <span className="chip info">{titleCase(note.kind)}</span>
                                        <span style={{ fontSize: "0.75rem", color: "var(--muted)" }}>
                                            {formatDate(note.note_date)} · {employeeName(note.employee_id)}
                                        </span>
                                    </div>
                                    <p style={{ fontSize: "0.85rem", color: "var(--ink-soft)", marginTop: "0.3rem", lineHeight: 1.55 }}>
                                        {note.body}
                                    </p>
                                    {note.follow_up && (
                                        <p style={{ fontSize: "0.8rem", color: "var(--warn)", marginTop: "0.35rem" }}>
                                            Follow-up: {note.follow_up}
                                        </p>
                                    )}
                                </div>
                            ))}
                            {theirNotes.length === 0 && (
                                <p style={{ color: "var(--muted)", fontSize: "0.85rem" }}>No service notes recorded.</p>
                            )}
                        </div>
                    </div>
                )}

                {dialog?.kind === "document" && (
                    <DocumentDialog participant={dialog.participant} defaultKind={dialog.docKind}
                        onClose={() => setDialog(null)}
                        onSaved={() => { setDialog(null); participantDocs.reload(); }} />
                )}

                {tab === "documents" && (
                    <div style={{ display: "grid", gap: "1rem" }}>
                        <div className="card">
                            <div className="card-head">
                                <h2>Care plans, consents &amp; documents</h2>
                                {canManage && (
                                    <button className="btn small"
                                        onClick={() => setDialog({ kind: "document", participant: selected, docKind: "care_plan" })}>
                                        + Add document
                                    </button>
                                )}
                            </div>
                            <div className="card-body flush">
                                {participantDocs.error && <div className="error-banner" style={{ margin: "0.75rem" }}>{participantDocs.error}</div>}
                                <DocumentsTable docs={generalDocs} />
                            </div>
                        </div>
                        <div className="card">
                            <div className="card-head">
                                <h2>Shift-attached care plans</h2>
                                <span className="chip mute">attached in Rostering, visible around each shift</span>
                            </div>
                            <div className="card-body flush">
                                <div className="table-wrap"><table>
                                    <thead><tr><th>File</th><th>For shift</th><th>Attached</th></tr></thead>
                                    <tbody>
                                        {(shiftCarePlans.data ?? []).map((plan) => (
                                            <tr key={plan.id}>
                                                <td>
                                                    <a href={fileViewUrl(plan.id)} target="_blank" rel="noreferrer" style={{ fontWeight: 600 }}>
                                                        {plan.file_name}
                                                    </a>
                                                </td>
                                                <td className="mono">{formatDateTime(plan.shift_starts_at)}</td>
                                                <td className="mono">{formatDate(plan.created_at)}</td>
                                            </tr>
                                        ))}
                                        {(shiftCarePlans.data ?? []).length === 0 && (
                                            <tr><td colSpan={3} className="empty">No shift-attached care plans.</td></tr>
                                        )}
                                    </tbody>
                                </table></div>
                            </div>
                        </div>
                    </div>
                )}

                {tab === "risk" && (
                    <div className="card">
                        <div className="card-head">
                            <h2>Risk assessments</h2>
                            {canManage && (
                                <button className="btn small"
                                    onClick={() => setDialog({ kind: "document", participant: selected, docKind: "risk_assessment" })}>
                                    + Add risk assessment
                                </button>
                            )}
                        </div>
                        <div className="card-body flush">
                            <DocumentsTable docs={riskDocs} />
                        </div>
                    </div>
                )}

                {tab === "invoices" && canManage && (
                    <div className="card">
                        <div className="card-head">
                            <h2>Invoices covering this participant</h2>
                            <span className="chip mute">billed to their provider</span>
                        </div>
                        <div className="card-body flush">
                            <div className="table-wrap"><table>
                                <thead><tr><th>Invoice</th><th>Provider</th><th>Period</th><th>Status</th>
                                    <th style={{ textAlign: "right" }}>Total</th></tr></thead>
                                <tbody>
                                    {(participantInvoices.data ?? []).map((invoice) => (
                                        <tr key={invoice.id}>
                                            <td className="mono" style={{ fontWeight: 600 }}>{invoice.invoice_number}</td>
                                            <td>{invoice.company_name}</td>
                                            <td className="mono">{formatDate(invoice.period_start)} – {formatDate(invoice.period_end)}</td>
                                            <td><span className={`chip ${invoice.status === "paid" ? "pass" : invoice.status === "draft" ? "mute" : "info"}`}>{invoice.status}</span></td>
                                            <td className="mono" style={{ textAlign: "right", fontWeight: 600 }}>
                                                {Number(invoice.total).toLocaleString("en-AU", { style: "currency", currency: "AUD" })}
                                            </td>
                                        </tr>
                                    ))}
                                    {(participantInvoices.data ?? []).length === 0 && (
                                        <tr><td colSpan={5} className="empty">No invoices include this participant's shifts yet.</td></tr>
                                    )}
                                </tbody>
                            </table></div>
                        </div>
                    </div>
                )}

                {tab === "forms" && (
                    <>
                        {fillingTemplate && (
                            <FormFillDialog template={fillingTemplate}
                                participantId={selected.id} participantName={selected.full_name}
                                onClose={() => setFillingTemplate(null)}
                                onDone={() => { setFillingTemplate(null); formSubmissions.reload(); }} />
                        )}
                        <div className="card">
                            <div className="card-head"><h2>Start a form</h2></div>
                            <div className="card-body" style={{ display: "grid", gap: "0.8rem" }}>
                                {[...new Set(publishedTemplates.map((t) => t.category ?? "Other"))].sort().map((category) => (
                                    <div key={category}>
                                        <div className="doc-label" style={{ marginBottom: "0.35rem" }}>{category}</div>
                                        <div style={{ display: "flex", flexWrap: "wrap", gap: "0.4rem" }}>
                                            {publishedTemplates
                                                .filter((t) => (t.category ?? "Other") === category)
                                                .map((t) => (
                                                    <button key={t.id} className="btn ghost small"
                                                        onClick={() => setFillingTemplate(t)}>
                                                        {t.name} <span className="mono" style={{ opacity: 0.6 }}>{t.code}</span>
                                                    </button>
                                                ))}
                                        </div>
                                    </div>
                                ))}
                                {publishedTemplates.length === 0 && (
                                    <p style={{ color: "var(--muted)", fontSize: "0.85rem" }}>
                                        No published form templates yet — build them under Forms.
                                    </p>
                                )}
                            </div>
                        </div>
                        <div className="card">
                            <div className="card-head"><h2>Submitted about {selected.full_name}</h2></div>
                            <div className="card-body flush">
                                <div className="table-wrap">
                                    <table>
                                        <thead><tr><th>Date</th><th>Form</th><th>Summary</th><th>Status</th></tr></thead>
                                        <tbody>
                                            {theirForms.map((s) => {
                                                const t = templateById.get(s.template_id);
                                                return (
                                                    <tr key={s.id}>
                                                        <td className="mono">{formatDate(s.created_at)}</td>
                                                        <td style={{ fontWeight: 600 }}>{t ? `${t.code} — ${t.name}` : "—"}</td>
                                                        <td style={{ color: "var(--ink-soft)" }}>{s.summary}</td>
                                                        <td><span className={`chip ${s.status === "reviewed" ? "pass" : "warn"}`}>{titleCase(s.status)}</span></td>
                                                    </tr>
                                                );
                                            })}
                                            {theirForms.length === 0 && (
                                                <tr><td colSpan={4} className="empty">No forms submitted about this participant yet.</td></tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </>
                )}

                {tab === "settings" && canManage && (
                    <div className="grid cols-2">
                        <ParticipantPhotoCard participant={selected}
                            onSaved={() => participants.reload()} />
                        <ParticipantComplianceCard participant={selected}
                            onSaved={() => participants.reload()} />
                        <StringListCard title="Default shift tasks"
                            help="Seeded onto every NEW shift for this participant as a checklist the worker ticks off. Existing shifts keep their tasks."
                            items={selected.default_shift_tasks ?? []}
                            addLabel="Add task" placeholder="e.g. Check pressure areas"
                            onSave={async (items) => {
                                await api.put(`/participants/${selected.id}`, { default_shift_tasks: items });
                                participants.reload();
                            }} />
                        <StringListCard title="Shift-end questions"
                            help="Questions the worker answers when closing a shift for this participant."
                            items={selected.shift_end_questions ?? []}
                            addLabel="Add question" placeholder="e.g. Did the participant eat well?"
                            onSave={async (items) => {
                                await api.put(`/participants/${selected.id}`, { shift_end_questions: items });
                                participants.reload();
                            }} />
                        <div className="card">
                            <div className="card-head"><h2>Record settings</h2></div>
                            <div className="card-body" style={{ display: "grid", gap: "0.8rem" }}>
                                <dl className="kv">
                                    <dt>Status</dt>
                                    <dd><span className={`chip ${selected.status === "active" ? "pass" : "mute"}`}>{titleCase(selected.status)}</span></dd>
                                    <dt>Provider</dt>
                                    <dd>{selected.company_id ? "Linked to a provider organisation" : "No provider linked"}</dd>
                                    <dt>Plan period</dt>
                                    <dd>{formatDate(selected.plan_start_date)} – {formatDate(selected.plan_end_date)}</dd>
                                </dl>
                                <div style={{ display: "flex", gap: "0.5rem" }}>
                                    <button className="btn small" onClick={() => setDialog({ kind: "edit", participant: selected })}>
                                        Edit details
                                    </button>
                                    <button className="btn ghost small" onClick={() => setDialog({ kind: "delete", participant: selected })}>
                                        Discharge
                                    </button>
                                </div>
                            </div>
                        </div>
                        <div className="card">
                            <div className="card-head"><h2>Worker exclusions</h2></div>
                            <div className="card-body">
                                <p style={{ fontSize: "0.82rem", color: "var(--muted)" }}>
                                    Preferred and excluded workers are managed on the Assigned workers
                                    tab. An exclusion is absolute — rule R8 refuses the roster with no
                                    override, for any role.
                                </p>
                                <button className="btn ghost small" style={{ marginTop: "0.6rem" }} onClick={() => setTab("workers")}>
                                    Open assigned workers
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {tab === "handovers" && (
                    <div className="card">
                        <div className="card-head">
                            <h2>Handovers</h2>
                            {canRecordClinical && (
                                <button className="btn small" onClick={() => setDialog({ kind: "handover", participant: selected })}>
                                    + Create handover
                                </button>
                            )}
                        </div>
                        <div className="card-body" style={{ display: "grid", gap: "0.9rem" }}>
                            {theirHandovers.map((handover) => (
                                <div key={handover.id} className="card" style={{ boxShadow: "none" }}>
                                    <div className="card-head">
                                        <div>
                                            <strong style={{ display: "flex", alignItems: "center", gap: "0.4rem", fontSize: "0.88rem" }}>
                                                <InitialsAvatar name={employeeName(handover.from_employee_id)} size={22} />
                                                {employeeName(handover.from_employee_id)}
                                                {" → "}
                                                <InitialsAvatar name={employeeName(handover.to_employee_id)} size={22} />
                                                {employeeName(handover.to_employee_id)}
                                            </strong>
                                            <div style={{ fontSize: "0.75rem", color: "var(--muted)" }}>
                                                {formatDateTime(handover.occurred_at)}
                                            </div>
                                        </div>
                                        <div style={{ display: "flex", gap: "0.4rem", alignItems: "center" }}>
                                            <span className={`chip ${handover.status === "acknowledged" ? "pass" : handover.status === "issued" ? "warn" : "mute"}`}>
                                                {handover.status}
                                            </span>
                                            {handover.status === "issued" && canRecordClinical && (
                                                <button className="icon-btn" onClick={() => setDialog({ kind: "acknowledge", handover })}>
                                                    Acknowledge
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                    <div className="card-body">
                                        <dl className="kv">
                                            <dt>Summary</dt><dd>{handover.summary}</dd>
                                            {handover.clinical_changes && (<><dt>Clinical changes</dt><dd>{handover.clinical_changes}</dd></>)}
                                            {handover.risks && (<><dt>Risks</dt><dd>{handover.risks}</dd></>)}
                                            {handover.actions_required && (<><dt>Actions</dt><dd>{handover.actions_required}</dd></>)}
                                            {handover.acknowledged_at && (
                                                <><dt>Acknowledged</dt><dd>{formatDateTime(handover.acknowledged_at)}</dd></>
                                            )}
                                        </dl>
                                    </div>
                                </div>
                            ))}
                            {theirHandovers.length === 0 && (
                                <p style={{ color: "var(--muted)", fontSize: "0.85rem" }}>No handovers recorded.</p>
                            )}
                        </div>
                    </div>
                )}
            </div>
        );
    }

    const filtered = (participants.data ?? []).filter((p) =>
        matches(search, p.full_name, p.ndis_number, p.suburb) &&
        (!filters.status || p.status === filters.status) &&
        (!filters.support || p.service_supports.includes(filters.support)) &&
        (!filters.provider || p.company_id === filters.provider)
    );

    // Directory rollups — all derived live, nothing stored.
    const providerName = new Map((companies.data ?? []).map((c) => [c.id, c.name]));
    const budgetsByPart = new Map<string, { alloc: number; spent: number }>();
    for (const b of allBudgets.data ?? []) {
        const cur = budgetsByPart.get(b.participant_id) ?? { alloc: 0, spent: 0 };
        cur.alloc += Number(b.allocated); cur.spent += Number(b.spent);
        budgetsByPart.set(b.participant_id, cur);
    }
    const utilOf = (id: string): number | null => {
        const b = budgetsByPart.get(id);
        return b && b.alloc > 0 ? Math.round((b.spent / b.alloc) * 100) : null;
    };
    const openIncidents = (allIncidents.data ?? []).filter((i) => i.status === "open" || i.status === "under_review");
    const openIncidentsFor = (id: string) => openIncidents.filter((i) => i.participant_id === id).length;
    const isIncomplete = (p: Participant) => !p.ndis_number || !p.plan_start_date || !p.plan_end_date;
    const activeParts = (participants.data ?? []).filter((p) => p.status === "active");
    const expiringPlans = activeParts.filter((p) => p.plan_end_date && daysUntil(p.plan_end_date) >= 0 && daysUntil(p.plan_end_date) < 60).length;
    const utils = activeParts.map((p) => utilOf(p.id)).filter((u): u is number => u != null);
    const avgUtil = utils.length ? Math.round(utils.reduce((a, u) => a + u, 0) / utils.length) : null;

    return (
        <div>
            {dialogs}

            {canManage && (registrations.data ?? []).length > 0 && (
                <div className="card" style={{ marginBottom: "0.9rem" }}>
                    <div className="card-head">
                        <div>
                            <h2>Provider registrations awaiting review</h2>
                            <p style={{ fontSize: "0.76rem", color: "var(--muted)", marginTop: "0.15rem" }}>
                                Submitted by partner organisations from their portal. Approving creates the
                                participant linked to that provider; nothing enters the caseload unreviewed.
                            </p>
                        </div>
                        <span className="chip warn">{(registrations.data ?? []).length} pending</span>
                    </div>
                    <div className="card-body" style={{ display: "grid", gap: "0.45rem" }}>
                        {(registrations.data ?? []).map((r) => (
                            <div key={r.id} className="file-row" style={{ alignItems: "flex-start" }}>
                                <span className="mono" style={{ whiteSpace: "nowrap" }}>{formatDate(r.created_at)}</span>
                                <span>
                                    <div style={{ fontWeight: 600 }}>
                                        {r.first_name} {r.last_name}
                                        <span className="chip info" style={{ marginLeft: 6 }}>{r.company_name}</span>
                                    </div>
                                    <div style={{ fontSize: "0.75rem", color: "var(--muted)" }}>
                                        {[r.ndis_number && `NDIS ${r.ndis_number}`, r.date_of_birth && `DOB ${formatDate(r.date_of_birth)}`,
                                          r.phone, r.suburb].filter(Boolean).join(" · ") || "No extra details"}
                                    </div>
                                    {r.services.length > 0 && (
                                        <div style={{ marginTop: 3 }}>
                                            {r.services.map((s) => <span key={s} className="chip info" style={{ marginRight: 3 }}>{s}</span>)}
                                        </div>
                                    )}
                                    {r.notes && (
                                        <div style={{ fontSize: "0.78rem", color: "var(--ink-soft)", marginTop: 3, whiteSpace: "pre-wrap" }}>
                                            {r.notes}
                                        </div>
                                    )}
                                </span>
                                <span style={{ display: "flex", gap: "0.35rem", whiteSpace: "nowrap" }}>
                                    <button className="icon-btn" disabled={regBusyId === r.id}
                                        onClick={() => void decideRegistration(r.id, "approve")}>
                                        {regBusyId === r.id ? "Working…" : "Approve"}
                                    </button>
                                    <button className="icon-btn danger" disabled={regBusyId === r.id}
                                        onClick={() => {
                                            const reason = window.prompt("Reason for rejecting (the provider will read it):");
                                            if (reason && reason.trim().length >= 5) void decideRegistration(r.id, "reject", reason.trim());
                                        }}>
                                        Reject
                                    </button>
                                </span>
                            </div>
                        ))}
                        {regError && <div className="error-banner">{regError}</div>}
                    </div>
                </div>
            )}

            <div className="stat-strip">
                <div className="stat good"><div className="num">{activeParts.length}</div><div className="lbl">Participants</div></div>
                <div className={`stat ${activeParts.filter(isIncomplete).length ? "warn" : "good"}`}>
                    <div className="num">{activeParts.filter(isIncomplete).length}</div><div className="lbl">Incomplete records</div></div>
                <div className={`stat ${expiringPlans ? "warn" : "good"}`}>
                    <div className="num">{expiringPlans}</div><div className="lbl">Plans expiring &lt; 60 days</div></div>
                {canSeeFunding && (
                    <div className={`stat ${avgUtil != null && avgUtil >= 85 ? "warn" : "good"}`}>
                        <div className="num">{avgUtil != null ? `${avgUtil}%` : "—"}</div><div className="lbl">Avg budget utilisation</div></div>
                )}
                <div className={`stat ${openIncidents.length ? "warn" : "good"}`}>
                    <div className="num">{openIncidents.length}</div><div className="lbl">Open incidents</div></div>
            </div>

            <FilterBar
                search={search} onSearch={setSearch}
                searchPlaceholder="Search participants…"
                values={filters}
                onChange={(name, value) => setFilters((f) => ({ ...f, [name]: value }))}
                filters={[
                    { name: "status", allLabel: "All Status", options: PARTICIPANT_STATUS },
                    { name: "support", allLabel: "All Services", options: SUPPORTS },
                    ...(canManage ? [{
                        name: "provider", allLabel: "All Providers",
                        options: (companies.data ?? []).map((c) => ({ value: c.id, label: c.name })),
                    }] : []),
                ]}
                resultNote={
                    filtered.length !== (participants.data?.length ?? 0)
                        ? `Showing ${filtered.length} of ${participants.data?.length ?? 0} participants`
                        : undefined
                }
                action={canManage && (
                    <button className="btn" onClick={() => setDialog({ kind: "create" })}>+ Add New Participant</button>
                )}
            />

            <div className="card">
                <div className="card-body flush">
                    <div className="table-wrap">
                        <table>
                            <thead>
                                <tr>
                                    <th>Participant</th><th>Provider</th><th>NDIS plan</th>
                                    {canSeeFunding && <th>Budget utilisation</th>}
                                    <th>Coordinator</th><th>Record</th><th>Workers</th><th>Incidents</th><th>Status</th>
                                    {canManage && <th style={{ textAlign: "right" }}>Actions</th>}
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map((participant) => {
                                    const workers = workersFor(participant.id);
                                    const planDays = participant.plan_end_date ? daysUntil(participant.plan_end_date) : null;
                                    const util = utilOf(participant.id);
                                    const inc = openIncidentsFor(participant.id);
                                    return (
                                        <tr key={participant.id} className="clickable" onClick={() => { setSelected(participant); setTab("overview"); }}>
                                            <td>
                                                <div style={{ display: "flex", alignItems: "center", gap: "0.6rem" }}>
                                                    <InitialsAvatar name={participant.full_name} size={28} />
                                                    <div>
                                                        <div style={{ fontWeight: 600 }}>{participant.full_name}</div>
                                                        <div className="mono" style={{ fontSize: "0.72rem", color: "var(--muted)" }}>
                                                            {participant.ndis_number ? `NDIS ${participant.ndis_number}` : "NDIS number missing"}
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                {participant.company_id
                                                    ? <>{providerName.get(participant.company_id) ?? "—"}
                                                        <div><span className="chip info" style={{ fontSize: "0.68rem" }}>Provider-linked</span></div></>
                                                    : <>TrustCare
                                                        <div><span className="chip mute" style={{ fontSize: "0.68rem" }}>TrustCare-added</span></div></>}
                                            </td>
                                            <td>
                                                {planDays == null
                                                    ? <span className="chip mute">No plan on file</span>
                                                    : planDays < 0
                                                        ? <span className="chip block">Plan expired</span>
                                                        : planDays < 60
                                                            ? <span className="chip warn">Expires in {planDays} days</span>
                                                            : <span className="chip pass">{planDays} days left</span>}
                                                {participant.plan_start_date && participant.plan_end_date && (
                                                    <div style={{ fontSize: "0.72rem", color: "var(--muted)" }}>
                                                        {formatDate(participant.plan_start_date)} – {formatDate(participant.plan_end_date)}
                                                    </div>
                                                )}
                                            </td>
                                            {canSeeFunding && (
                                                <td>
                                                    {util == null ? <span className="muted">—</span> : (
                                                        <div className="meter" style={{ width: 130 }}>
                                                            <div className="meter-track">
                                                                <div className="meter-fill" style={{
                                                                    width: `${Math.min(100, util)}%`,
                                                                    background: util >= 95 ? "var(--block)" : util >= 85 ? "var(--warn)" : undefined,
                                                                }} />
                                                            </div>
                                                            <span className="meter-value">{util}% used</span>
                                                        </div>
                                                    )}
                                                </td>
                                            )}
                                            <td>{(() => {
                                                const cid = (participant as Participant & { coordinator_employee_id?: string | null }).coordinator_employee_id;
                                                return cid ? (employees.data ?? []).find((e) => e.id === cid)?.full_name ?? "—" : "—";
                                            })()}</td>
                                            <td>
                                                {isIncomplete(participant)
                                                    ? <span className="chip warn">Incomplete</span>
                                                    : <span className="chip pass">Complete</span>}
                                            </td>
                                            <td>
                                                {workers.length}
                                                {workers.some((w) => w.assignment_type === "excluded") && (
                                                    <span className="chip block" style={{ marginLeft: 5 }}>has exclusion</span>
                                                )}
                                            </td>
                                            <td>
                                                {inc > 0
                                                    ? <span className="chip block">{inc} open</span>
                                                    : <span className="muted">—</span>}
                                            </td>
                                            <td>
                                                <span className={`chip ${participant.status === "active" ? "pass" : "mute"}`}>
                                                    {titleCase(participant.status)}
                                                </span>
                                            </td>
                                            {canManage && (
                                                <td onClick={(e) => e.stopPropagation()}>
                                                    <div className="row-actions">
                                                        <button className="icon-btn" onClick={() => setDialog({ kind: "edit", participant })}>Edit</button>
                                                        <button className="icon-btn" onClick={() => setDialog({ kind: "assign", participant })}>Assign</button>
                                                        <button className="icon-btn danger" onClick={() => setDialog({ kind: "delete", participant })}>Discharge</button>
                                                    </div>
                                                </td>
                                            )}
                                        </tr>
                                    );
                                })}
                                {filtered.length === 0 && (
                                    <tr><td colSpan={10} className="empty">No participants match.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
};
